import { Button } from 'components';
import {
  Cell,
  ExpandRowWrapper,
  Row,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from 'components/FlexTable/Table.styled';
import { TableSpinner } from 'components/TableSpinner';
import { TextNoData } from 'components/TextNoData';
import { ImportLogItem } from 'graphql/query/useQueryImportLogs';
import { useState } from 'react';
import styled from 'styled-components';
import { Box, Flex, Icon, Text } from 'ui';
import { formatDate } from 'utils';
import { ImportLogExpandRow } from './ImportLogExpandRow';

type ImportLogsTableProps = {
  data: ImportLogItem[];
  loading: boolean;
};

export const ImportLogsTable = ({ data, loading }: ImportLogsTableProps) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const handleExpand = (id: string) => {
    setExpandedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((row) => row !== id);
      }
      return [...prev, id];
    });
  };

  return (
    <Flex pt="s10" mb="s40" flexDirection="column" flex="1">
      <Table>
        <ImportLogsHead />
        <TableBody>
          {data?.length > 0 || loading ? (
            data?.map((item) => (
              <ImportLogRow
                key={item.id}
                item={item}
                isExpanded={expandedRows.includes(item.id)}
                handleExpand={handleExpand}
              />
            ))
          ) : (
            <Box m="s130">
              <TextNoData />
            </Box>
          )}
          <TableSpinner loading={loading} />
        </TableBody>
      </Table>
    </Flex>
  );
};

const ImportLogRow = ({
  item,
  isExpanded,
  handleExpand,
}: {
  item: ImportLogItem;
  isExpanded: boolean;
  handleExpand: (id: string) => void;
}) => (
  <ExpandRowWrapper>
    <Row isExpanded={isExpanded}>
      <Cell flex={120}>{item.importer}</Cell>
      <Cell flex={160}>{item.file || '-'}</Cell>
      <Cell flex={100}>{item.parsedCount}</Cell>
      <Cell flex={100}>{item.importedCount}</Cell>
      <Cell flex={100}>
        <Text color={item.errorItemsCount > 0 ? 'alert50' : undefined}>
          {item.errorItemsCount}
        </Text>
      </Cell>
      <Cell flex={120}>{formatDate(item.createdAt) || '-'}</Cell>
      <Cell flex={80} onClick={() => handleExpand(item.id)}>
        <Button fontWeight="regular" variant="text">
          View more
        </Button>
        <StyledIcon
          isExpanded={isExpanded}
          color="gray80"
          ml="s10"
          name="16-arrowhead"
        />
      </Cell>
    </Row>
    {isExpanded && <ImportLogExpandRow id={item.id} />}
  </ExpandRowWrapper>
);

const ImportLogsHead = () => (
  <TableHead>
    <TableHeadRow>
      <TableHeadCell flex={120}>Importer</TableHeadCell>
      <TableHeadCell flex={160}>File</TableHeadCell>
      <TableHeadCell flex={100}>Parsed</TableHeadCell>
      <TableHeadCell flex={100}>Imported</TableHeadCell>
      <TableHeadCell flex={100}>Errors</TableHeadCell>
      <TableHeadCell flex={120}>Date</TableHeadCell>
      <TableHeadCell flex={80}>Details</TableHeadCell>
    </TableHeadRow>
  </TableHead>
);

const StyledIcon = styled(Icon)<{ isExpanded: boolean }>`
  transform: ${({ isExpanded }) =>
    isExpanded ? 'rotate(0)' : 'rotate(180deg)'};
`;
